import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import DataTable from "../../../components/shared/DataTable";
import { formatDate, statusBadgeClass } from "../../../utils/helpers";
import api from "../../../utils/api";

export default function AppointmentList() {
  const queryClient = useQueryClient();
  const [view, setView] = useState("today"); // "today" | "chamber" | "all"
  const [selectedChamber, setSelectedChamber] = useState("");

  const { data: chambers } = useQuery("doctor-chambers", () =>
    api.get("/doctor/chambers")
  );

  const { data, isLoading } = useQuery(
    ["doctor-appointments", view, selectedChamber],
    () =>
      api.get(
        `/doctor/appointments?view=${view}&chamberId=${selectedChamber}`
      )
  );

  const updateStatusMutation = useMutation(
    ({ id, status }) => api.post(`/doctor/appointments/${id}/status`, { status }),
    {
      onSuccess: (_, { status }) => {
        queryClient.invalidateQueries("doctor-appointments");
        toast.success(`Appointment ${status}`);
      },
    }
  );

  const rescheduleMutation = useMutation(
    ({ id, newSlot }) => api.post(`/doctor/appointments/${id}/reschedule`, { newSlot }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("doctor-appointments");
        toast.success("Appointment rescheduled");
      },
    }
  );

  const columns = [
    { key: "patientName", label: "Patient" },
    { key: "forName", label: "For" },
    {
      key: "scheduledAt",
      label: "Scheduled",
      render: (row) => formatDate(row.scheduledAt, "dd MMM, hh:mm a"),
      sortable: true,
    },
    { key: "chamberName", label: "Chamber" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className={statusBadgeClass(row.status)}>{row.status}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) =>
        row.status === "confirmed" || row.status === "pending" ? (
          <div className="flex gap-2">
            <button
              onClick={() => updateStatusMutation.mutate({ id: row.id, status: "completed" })}
              className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
            >
              Done
            </button>
            <button
              onClick={() => updateStatusMutation.mutate({ id: row.id, status: "cancelled" })}
              className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
            >
              Cancel
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      {/* View Tabs */}
      <div className="flex gap-2 flex-wrap items-center">
        {[
          { key: "today", label: "Today" },
          { key: "all", label: "All" },
          { key: "chamber", label: "By Chamber" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setView(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              view === tab.key
                ? "bg-primary-600 border-primary-600 text-white"
                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
        {view === "chamber" && (
          <select
            value={selectedChamber}
            onChange={(e) => setSelectedChamber(e.target.value)}
            className="input-field w-auto"
          >
            <option value="">All Chambers</option>
            {(chambers?.chambers || []).map((ch) => (
              <option key={ch.id} value={ch.id}>{ch.location}</option>
            ))}
          </select>
        )}
      </div>

      <DataTable
        columns={columns}
        data={data?.appointments || []}
        loading={isLoading}
        emptyMessage="No appointments found."
      />
    </div>
  );
}
